import type { IFramework } from '@/framework';
import type { TextRenderResolution, TextRenderSnapshot } from '@/framework/services/text-render';
import {
    TextVerticalAlign,
    TextWritingType,
    type ILayerProps,
    type IPoint,
    type IVectorLayerProps,
    type TextContentPadding,
} from '@/version-types';
import type { IEdgelessVectorLayer } from '../types';

/** Vector Text 未显式设置 padding 时使用的零内边距。 */
const ZERO_PADDING: TextContentPadding = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * 把 verticalAlign 归一化为交叉轴剩余空间的分配权重。
 *
 * @param align - Vector Text 的 verticalAlign。
 * @returns top 为 0、center 为 0.5、bottom 为 1 的权重。
 */
const resolveAlignWeight = (align: TextVerticalAlign): number => {
    if (align === TextVerticalAlign.BOTTOM) return 1;
    if (align === TextVerticalAlign.CENTER) return 0.5;
    return 0;
};

/**
 * 根据 Vector Text 的交叉轴尺寸变化计算锚点位移。
 *
 * @param delta - 新旧交叉轴尺寸差。
 * @param align - Vector Text 的 verticalAlign。
 * @param isHorizontal - 是否为横排文字。
 * @returns 应加到 top 或 left 的位移。
 */
const resolveAnchorOffset = (delta: number, align: TextVerticalAlign, isHorizontal: boolean): number => {
    if (isHorizontal) {
        if (align === TextVerticalAlign.BOTTOM) return -delta;
        if (align === TextVerticalAlign.CENTER) return -delta / 2;
        return 0;
    }

    if (align === TextVerticalAlign.TOP) return -delta;
    if (align === TextVerticalAlign.CENTER) return -delta / 2;
    return 0;
};

/**
 * 计算 Vector Text 内容框在行内轴上的可用尺寸。
 *
 * 横排的行内轴是宽度，竖排是高度；两者都需要扣除对应方向的 padding。
 *
 * @param layer - 当前带有 textInfo 的 Vector 工作值。
 * @returns 扣除 padding 后的行内尺寸；无 textInfo 时返回 0。
 */
export const resolveVectorTextInlineSize = (layer: ILayerProps): number => {
    const textInfo = (layer.data as IVectorLayerProps).textInfo;
    if (!textInfo) return 0;

    const padding = textInfo.padding ?? ZERO_PADDING;

    return textInfo.writingMode === TextWritingType.HORIZONTAL_TB
        ? Math.max(layer.width - padding.left - padding.right, 0)
        : Math.max(layer.height - padding.top - padding.bottom, 0);
};

/**
 * 计算 Text Render 快照坐标系到 Vector 图层坐标系的平移量。
 *
 * padding 决定内容框位置，verticalAlign 决定内容框内剩余交叉轴空间的分配；竖排快照的
 * 行内原点位于内容框右边界，因此额外补一个 contentWidth 把负坐标搬回图层坐标系。
 * 组合渲染与编辑几何必须共用该公式，光标、选区和正文才会对齐同一份快照。
 *
 * @param layer - 当前带有 textInfo 的 Vector 工作值。
 * @param snapshot - 当前工作值对应的排版快照。
 * @returns 快照坐标加上该偏移即为图层局部坐标。
 */
export const resolveVectorTextContentOffset = (layer: ILayerProps, snapshot: TextRenderSnapshot | null): IPoint => {
    const textInfo = (layer.data as IVectorLayerProps).textInfo;
    if (!textInfo || !snapshot) return { x: 0, y: 0 };

    const padding = textInfo.padding ?? ZERO_PADDING;
    const isVertical = textInfo.writingMode === TextWritingType.VERTICAL_RL;
    const alignWeight = resolveAlignWeight(textInfo.verticalAlign);
    const crossSpace = Math.max(
        0,
        isVertical
            ? layer.width - padding.left - padding.right - snapshot.contentWidth
            : layer.height - padding.top - padding.bottom - snapshot.contentHeight,
    );

    return {
        x: padding.left + (isVertical ? crossSpace * (1 - alignWeight) + snapshot.contentWidth : 0),
        y: padding.top + (isVertical ? 0 : crossSpace * alignWeight),
    };
};

/**
 * 为正在编辑的空 Vector Text 创建宿主专属的 caret 几何。
 *
 * Text Render 的 empty 状态不发布快照，只有编辑宿主需要用当前内容框补出空几何；
 * 交叉轴使用单行高度而不是整个内容框，verticalAlign 才能把空光标放到正确锚点。
 *
 * @param layer - 当前 Vector Text 编辑工作值。
 * @returns 空文字时返回最小可用快照；有文字或无 textInfo 时返回 null。
 */
export const createEmptyVectorEditingSnapshot = (layer: ILayerProps): TextRenderSnapshot | null => {
    const textInfo = (layer.data as IVectorLayerProps).textInfo;
    if (!textInfo || textInfo.text !== '') return null;

    const isHorizontal = textInfo.writingMode === TextWritingType.HORIZONTAL_TB;
    const lineSize = Math.max(Math.ceil(textInfo.fontSize * (textInfo.lineSpacing || 1.2)), 1);
    const inlineSize = Math.max(resolveVectorTextInlineSize(layer), 1);

    return {
        writingMode: textInfo.writingMode as TextRenderSnapshot['writingMode'],
        instructions: [],
        glyphInfos: [],
        lineMetrics: [],
        spanRanges: [],
        listMarkerIndent: 0,
        contentWidth: isHorizontal ? inlineSize : lineSize,
        contentHeight: isHorizontal ? lineSize : inlineSize,
        minInlineSize: inlineSize,
    };
};

/**
 * 将 Text Renderer 的布局结果应用到 Vector 的逻辑几何。
 *
 * 横排只自适应高度，竖排只自适应宽度；padding 计入最终交叉轴尺寸，且结果不小于
 * 用户最后一次主动调整记录的 proactive 尺寸。pending、failed 与 empty 均保留原几何。
 *
 * @param layer - 本轮包含最新 textInfo 的 Vector 工作值。
 * @param resolution - 由该真实 Vector 宿主 Renderer 返回的四态布局结果。
 * @returns 可与 textInfo 原子提交的 Vector 工作值。
 */
export const applyVectorTextLayout = (
    layer: ILayerProps,
    resolution: TextRenderResolution<TextRenderSnapshot>,
): ILayerProps => {
    if (resolution.status !== 'ready') return layer;

    const textInfo = (layer.data as IVectorLayerProps).textInfo;
    if (!textInfo) return layer;

    const padding = textInfo.padding ?? ZERO_PADDING;
    const isHorizontal = textInfo.writingMode === TextWritingType.HORIZONTAL_TB;
    const contentCrossSize = isHorizontal ? resolution.value.contentHeight : resolution.value.contentWidth;
    const paddingCrossSize = isHorizontal ? padding.top + padding.bottom : padding.left + padding.right;
    const proactiveCrossSize = isHorizontal ? layer.__proactiveHeight : layer.__proactiveWidth;
    const currentCrossSize = isHorizontal ? layer.height : layer.width;
    const nextCrossSize = Math.max(contentCrossSize + paddingCrossSize, proactiveCrossSize ?? 0);

    if (!Number.isFinite(nextCrossSize) || nextCrossSize <= 0 || nextCrossSize === currentCrossSize) return layer;

    const delta = nextCrossSize - currentCrossSize;
    const anchorOffset = resolveAnchorOffset(delta, textInfo.verticalAlign, isHorizontal);

    return isHorizontal
        ? { ...layer, top: layer.top + anchorOffset, height: nextCrossSize }
        : { ...layer, left: layer.left + anchorOffset, width: nextCrossSize };
};

/**
 * 获取当前图层对应的真实 Vector Edgeless 宿主。
 *
 * @param framework - 当前编辑器 framework。
 * @param layerId - 目标 Vector Layer ID。
 * @returns 已注册且公开 layout seam 的 Vector 宿主；尚未创建时返回 null。
 */
export const getVectorLayerHost = (framework: IFramework, layerId: string): IEdgelessVectorLayer | null => {
    const object = framework.service.edgelessCanvas.getByLayerId(layerId) as IEdgelessVectorLayer | null;
    return object && typeof object.layoutText === 'function' ? object : null;
};

/**
 * 使用真实 Vector 宿主同步解析候选文字，并应用唯一的布局公式。
 *
 * @param framework - 当前编辑器 framework。
 * @param layer - 带有最新 textInfo 的同 ID Vector 工作值。
 * @returns ready 时更新几何的工作值；empty、pending、failed 或宿主缺失时保留原几何。
 */
export const resolveVectorTextLayout = (framework: IFramework, layer: ILayerProps): ILayerProps => {
    const host = getVectorLayerHost(framework, layer.id);
    return host ? applyVectorTextLayout(layer, host.layoutText(layer)) : layer;
};

/**
 * 使用真实 Vector 宿主的异步排版入口准备字体依赖，并应用与同步 API 相同的布局公式。
 *
 * @param framework - 当前编辑器 framework。
 * @param layer - 带有最新 textInfo 的同 ID Vector 工作值。
 * @returns 依赖终态后完成几何计算的工作值；宿主缺失时保留原几何。
 */
export const resolveVectorTextLayoutAsync = async (framework: IFramework, layer: ILayerProps): Promise<ILayerProps> => {
    const host = getVectorLayerHost(framework, layer.id);
    if (!host) return layer;

    return applyVectorTextLayout(layer, await host.layoutTextAsync(layer));
};
